import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { searchApi } from "@/services/api-v2.service";
import type { SearchStats } from "@/types/api-v2";
import {
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Info,
  Trash2,
  Database
} from "lucide-react";

export default function SearchApiManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isTestingConnection, setIsTestingConnection] = useState(false);

  // Получение статистики сервиса поиска
  const { data: searchStats, isLoading: isLoadingStats } = useQuery<SearchStats>({
    queryKey: ['admin', 'search', 'stats'],
    queryFn: () => searchApi.getStats(),
    refetchInterval: 30000,
  });

  // Мутация для тестирования соединения
  const testConnectionMutation = useMutation({
    mutationFn: async () => {
      return await searchApi.testConnection();
    },
    onSuccess: (data) => {
      if (data.status === 'success') {
        toast({
          title: "Соединение успешно",
          description: `Сервис поиска доступен. Время ответа: ${data.responseTime || 0}мс`
        });
        queryClient.invalidateQueries({ queryKey: ['admin', 'search', 'stats'] });
      } else {
        toast({
          title: "Ошибка соединения",
          description: data.message || "Не удалось подключиться к сервису поиска",
          variant: "destructive"
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка соединения",
        description: error.message || "Не удалось подключиться к сервису поиска",
        variant: "destructive"
      });
    }
  });

  // Мутация для очистки логов
  const cleanupLogsMutation = useMutation({
    mutationFn: async () => {
      return await searchApi.cleanupLogs();
    },
    onSuccess: (data) => {
      toast({
        title: "Логи очищены",
        description: data.message || `Удалено ${data.deletedCount || 0} записей`
      });
      // Обновляем статистику после очистки
      queryClient.invalidateQueries({ queryKey: ['admin', 'search', 'stats'] });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось очистить логи",
        variant: "destructive"
      });
    }
  });

  const handleTestConnection = () => {
    setIsTestingConnection(true);
    testConnectionMutation.mutate();
    setTimeout(() => setIsTestingConnection(false), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-600 dark:text-green-400';
      case 'slow': return 'text-yellow-600 dark:text-yellow-400';
      case 'offline': return 'text-red-600 dark:text-red-400';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return <CheckCircle className="h-4 w-4" />;
      case 'slow': return <AlertTriangle className="h-4 w-4" />;
      case 'offline': return <AlertTriangle className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  // Функция для форматирования даты в человекочитаемый формат
  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Не указано';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Только что';
      
      if (diffMins < 60) {
        const mins = diffMins === 1 ? 'минуту' : diffMins < 5 ? 'минуты' : 'минут';
        return `${diffMins} ${mins} назад`;
      }
      
      if (diffHours < 24) {
        const hours = diffHours === 1 ? 'час' : diffHours < 5 ? 'часа' : 'часов';
        return `${diffHours} ${hours} назад`;
      }
      
      if (diffDays < 7) {
        const days = diffDays === 1 ? 'день' : diffDays < 5 ? 'дня' : 'дней';
        return `${diffDays} ${days} назад`;
      }
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="space-y-6 mb-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Управление сервисом поиска
              </CardTitle>
              <CardDescription>
                Мониторинг интеграции с сервисом поиска (Nominatim и др.)
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline" 
                size="sm"
                onClick={handleTestConnection}
                disabled={isTestingConnection || testConnectionMutation.isPending}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${isTestingConnection ? 'animate-spin' : ''}`} />
                Тест соединения
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingStats ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : searchStats ? (
            <div className="space-y-4">
              {/* Статистика API */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Всего запросов</div>
                  <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{searchStats.totalQueries}</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Среднее время ответа</div>
                  <div className="text-xl font-bold text-green-600 dark:text-green-400">{searchStats.averageResponseTime}мс</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Успешность</div>
                  <div className="text-xl font-bold text-purple-600 dark:text-purple-400">{searchStats.successRate}%</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Последний запрос</div>
                  <div className="text-sm font-bold text-orange-600 dark:text-orange-400">{formatDateTime(searchStats.lastQuery)}</div>
                </div>
              </div>

              {/* Статус API */}
              <div>
                <h4 className="font-medium mb-2 text-foreground">Статус сервиса</h4>
                <div className={`flex items-center gap-2 ${getStatusColor(searchStats.apiStatus)}`}>
                  {getStatusIcon(searchStats.apiStatus)}
                  <Badge variant={searchStats.apiStatus === 'online' ? 'default' : 'destructive'}>
                    {searchStats.apiStatus === 'online' ? 'В сети' : 
                     searchStats.apiStatus === 'slow' ? 'Медленно' : 'Не доступен'}
                  </Badge>
                </div>
              </div>

              {/* Конечные точки */}
              <div>
                <h4 className="font-medium mb-2 text-foreground">Конечная точка</h4>
                <div>
                  <span className="text-sm text-muted-foreground">Основная: </span>
                  <code className="text-xs rounded border border-border bg-muted/50 px-2 py-1 font-mono text-foreground break-all">{searchStats.endpoints.primary}</code>
                </div>
              </div>

              {/* Лимиты запросов */}
              <div>
                <h4 className="font-medium mb-2 text-foreground">Лимиты запросов</h4>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="text-sm text-foreground">
                    <span className="text-muted-foreground">Использовано: </span>
                    <span className="font-medium tabular-nums">{searchStats.rateLimit.current}/{searchStats.rateLimit.max}</span>
                  </div>
                  <div className="text-sm text-foreground">
                    <span className="text-muted-foreground">Сброс: </span>
                    <span className="font-medium">{formatDateTime(searchStats.rateLimit.resetTime)}</span>
                  </div>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary transition-[width]"
                    style={{
                      width: `${Math.min(
                        (searchStats.rateLimit.current / Math.max(searchStats.rateLimit.max, 1)) * 100,
                        100,
                      )}%`,
                    }}
                  />
                </div>
              </div>

              {/* Размер логов и очистка */}
              <div className="border-t border-border pt-4 mt-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <h4 className="font-medium flex items-center gap-2 text-foreground">
                    <Database className="h-4 w-4" />
                    Размер логов в БД
                  </h4>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      if (confirm('Вы уверены, что хотите удалить все логи? Это действие нельзя отменить.')) {
                        cleanupLogsMutation.mutate();
                      }
                    }}
                    disabled={cleanupLogsMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {cleanupLogsMutation.isPending ? 'Очистка...' : 'Очистить все логи'}
                  </Button>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm text-foreground">
                    <span className="font-medium text-muted-foreground">Общий размер: </span>
                    <span className="tabular-nums">{searchStats.tableSize?.totalSize || '0 bytes'}</span>
                  </div>
                  <div className="mt-1 text-sm text-foreground">
                    <span className="font-medium text-muted-foreground">Размер таблицы: </span>
                    <span className="tabular-nums">{searchStats.tableSize?.tableSize || '0 bytes'}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Нет данных о работе сервиса поиска
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

